import { useState, useEffect, useMemo } from "react";
import { Grid, CardContent } from "@mui/material";
import { IPartialAPISParty, IPartialAPISPartyOption, IRider, IFullFlight } from "@vmosolutions/oc-interfaces";

import { SelectField } from "src/components/FormikComponents/SelectField";
import { RidersAccordion } from "./RidersAccordion";
interface IManageRidersProps {
  id: string;
  paneSize?: number[];
  layout?: "vertical" | "horizontal";
  ridersOnFlight: IRider[];
  riderOptions: IPartialAPISPartyOption[];
  updateRidersOnFlight: (value: IRider[]) => void;
  flightId: string;
  menuPosition?: number;
  setLoadedFlight: (flightValues: IFullFlight) => void;
}
/**
 * Manage Operator related collections
 * @param props -
 */
export const ManageRiders = (props: IManageRidersProps): JSX.Element => {
  const {
    paneSize,
    layout,
    menuPosition,
    riderOptions,
    ridersOnFlight,
    updateRidersOnFlight,
    flightId,
    id,
    setLoadedFlight,
  } = props;

  // Determine main menu height based on layout.
  const maxMenuSize = useMemo(() => {
    const layoutBuffer = layout === "horizontal" ? 20 : -5;
    return paneSize && Array.isArray(paneSize) ? paneSize[1] - (100 + layoutBuffer) : 400;
  }, [paneSize, layout]);

  /////////////////////////
  // MANAGE RIDER LIST
  /////////////////////////
  const [riders, setRiders] = useState<IRider[]>([]);
  const [selectedRider, setSelectedRider] = useState<IPartialAPISParty>(null);

  const ridersOnFlightOptions: IPartialAPISPartyOption[] = useMemo(() => {
    if (ridersOnFlight.length && riderOptions.length) {
      return ridersOnFlight
        ?.map((rider: IRider) => {
          return riderOptions?.find((party: IPartialAPISPartyOption) => party._id === rider.apisparty_id);
        })
        .sort((a, b) => {
          return a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase());
        });
    } else {
      return [];
    }
  }, [riderOptions, ridersOnFlight]);

  //filter out marked for deletion party
  const filteredRidersOptions: IPartialAPISPartyOption[] = useMemo(() => {
    if (riderOptions.length > 0) {
      return riderOptions; //?.filter((party: IPartialAPISPartyOption) => !party.markedForDeletion);
    } else {
      return [];
    }
  }, [riderOptions]);

  useEffect(() => {
    setRiders(ridersOnFlight);
  }, [ridersOnFlight, setRiders]);

  // If users create, updated or delete operator, update list.
  const updateRiderList = (op: IRider, mode: "create" | "update" | "delete"): void => {
    const index = riders.findIndex((rider: IRider) => rider.apisparty_id === op.apisparty_id);
    const updatedRiders: IRider[] = [...riders];
    switch (true) {
      case mode === "create": {
        // Operator Created
        updatedRiders.push(op);
        const newRider = riderOptions.find((rider: IPartialAPISParty) => rider._id === op.apisparty_id);
        newRider.APISEmploymentCategory = op.APISEmploymentCategory;
        setSelectedRider(newRider);
        //make API call
        break;
      }
      case mode === "update" && index > -1: {
        // Operator Updated
        updatedRiders[index] = op;
        const updatedRider = riderOptions.find((rider: IPartialAPISParty) => rider._id === op.apisparty_id);
        updatedRider.APISEmploymentCategory = op.APISEmploymentCategory;
        setSelectedRider(updatedRider);
        //make API call
        break;
      }
      case mode === "delete" && index > -1: // Operator Deleted
        updatedRiders.splice(index, 1);
        setSelectedRider(null);
        break;
      default:
      // code block
    }

    //update the parent componet so the rider summary updates
    updateRidersOnFlight(updatedRiders);
  };

  return (
    <CardContent className="manage_rider">
      Manage Riders
      <Grid container direction="column" className="form_container">
        <Grid item className="select_rider" style={{ marginTop: menuPosition }}>
          <SelectField
            className="riders"
            name="riders"
            label=""
            maxMenuHeight={maxMenuSize}
            isMulti={false}
            value={selectedRider}
            onChange={setSelectedRider}
            options={ridersOnFlightOptions ?? []}
            getOptionLabel={(rider: IPartialAPISPartyOption) =>
              `${rider?.lastName}, ${rider?.firstName} ${rider?.employeeId ? ` (${rider.employeeId})` : ""}`
            }
            getOptionValue={(rider: IPartialAPISPartyOption) => rider?._id}
            placeholder="Select Rider"
            isSearchable={true}
            isClearable={true}
          />
        </Grid>

        <Grid item className="riders_accordion">
          <RidersAccordion
            selectedRider={selectedRider}
            ridersOptions={filteredRidersOptions}
            ridersOnFlight={ridersOnFlight}
            updateRiderList={updateRiderList}
            accordionProps={{ defaultExpanded: true }}
            flightId={flightId}
            windowId={id}
            setLoadedFlight={setLoadedFlight}
          />
        </Grid>
      </Grid>
    </CardContent>
  );
};
